import { useState, useEffect, useCallback, useMemo } from "react";
import {
    GripVerticalIcon,
    PlusIcon,
    TrashIcon,
    SaveIcon,
    InfoIcon,
    ChevronDownIcon,
    ChevronUpIcon,
} from "lucide-react";
import { Button } from "@component/button";
import { Card, CardContent } from "@component/card";
import { Tooltip } from "@component/tooltip";
import { getRubric, updateRubric } from "@service/training";
import type {
    RubricCriterion,
    TrainingType,
    RubricCategoryTemplate,
} from "@type/training";
import { RUBRIC_TEMPLATES } from "@type/training";
import { cn } from "@util/tailwind";

const WEIGHT_OPTIONS = [0.5, 1.0, 1.5, 2.0, 2.5, 3.0];

type ScoringGuide = { high: string; mid: string; low: string };

// The good-answer checkbox options and default scoring guides come from a rubric
// template (src/types/training.ts), keyed by criterion id. Building them off a
// template is what lets the editor show the right checkboxes for Customer
// Service vs Sales - or a client-specific template passed via `optionsTemplate`.
function correctAnswerOptionsFrom(template: RubricCategoryTemplate[]): Record<string, string[]> {
    return Object.fromEntries(template.map((t) => [t.id, t.goodAnswerOptions]));
}

function scoringGuidesFrom(template: RubricCategoryTemplate[]): Record<string, ScoringGuide> {
    return Object.fromEntries(template.map((t) => [t.id, t.scoringGuide]));
}

// Strips a "High score:" / "Mid score -" style prefix off a scoring-guide line
// so only the response body shows in the textarea. Tolerates ":", "-", and "—"
// separators - legacy rubrics were seeded with a hyphen, which earlier versions
// failed to strip, leaking the label into the body.
const SCORE_PREFIX = /^(?:High|Mid|Low) score\s*[-:—]\s*/;

function initCheckedMap(
    criteria: RubricCriterion[],
    optionsByCriterion: Record<string, string[]>,
): Record<string, boolean[]> {
    return Object.fromEntries(
        criteria.map((c) => {
            const options = optionsByCriterion[c.id] ?? [];
            if (!c.correct_answer) return [c.id, options.map(() => true)];
            const lines = c.correct_answer.split("\n").filter(Boolean);
            return [c.id, options.map((opt) => lines.includes(opt))];
        })
    );
}

function initCustomMap(
    criteria: RubricCriterion[],
    optionsByCriterion: Record<string, string[]>,
): Record<string, string> {
    return Object.fromEntries(
        criteria.map((c) => {
            const options = optionsByCriterion[c.id] ?? [];
            if (!c.correct_answer) return [c.id, ""];
            const lines = c.correct_answer.split("\n").filter(Boolean);
            return [c.id, lines.filter((l) => !options.includes(l)).join("\n")];
        })
    );
}

function initScoringMap(
    criteria: RubricCriterion[],
    guidesByCriterion: Record<string, ScoringGuide>,
): Record<string, ScoringGuide> {
    return Object.fromEntries(
        criteria.map((c) => {
            const defaults = guidesByCriterion[c.id] ?? { high: "", mid: "", low: "" };
            if (!c.scoring_guide) return [c.id, defaults];
            const lines = c.scoring_guide.split("\n");
            return [c.id, {
                high: lines[0]?.replace(SCORE_PREFIX, "") ?? defaults.high,
                mid: lines[1]?.replace(SCORE_PREFIX, "") ?? defaults.mid,
                low: lines[2]?.replace(SCORE_PREFIX, "") ?? defaults.low,
            }];
        })
    );
}

function assembleCorrectAnswer(
    criterionId: string,
    checked: boolean[],
    custom: string,
    optionsByCriterion: Record<string, string[]>,
): string {
    const options = optionsByCriterion[criterionId] ?? [];
    const selected = options.filter((_, i) => checked[i]);
    if (custom.trim()) selected.push(custom.trim());
    return selected.join("\n");
}

function assembleScoringGuide(guides: { high: string; mid: string; low: string }): string {
    return [
        `High score: ${guides.high}`,
        `Mid score: ${guides.mid}`,
        `Low score: ${guides.low}`,
    ].join("\n");
}

function createCriterion(): RubricCriterion {
    return {
        id: `criterion_${Date.now()}`,
        name: "",
        weight: 1.0,
        description: "",
        correct_answer: "",
        scoring_guide: "",
    };
}

// Controlled mode — parent owns the criteria state
interface ControlledProps {
    value: RubricCriterion[];
    onChange: (criteria: RubricCriterion[]) => void;
    readOnly?: boolean;
    showOnlyWeights?: boolean;
    stacked?: boolean;
    // Selects which rubric template's good-answer options / scoring guides the
    // customise UI shows. Defaults to "customer_service" for back-compat with
    // callsites (e.g. AIQM) that don't pass a type.
    trainingType?: TrainingType;
    // Overrides the checkbox options / scoring-guide defaults with a specific
    // template (e.g. a client rubric that isn't a public TrainingType). When set,
    // it takes precedence over `trainingType`.
    optionsTemplate?: RubricCategoryTemplate[];
    campaignId?: never;
    onSave?: never;
}

// API mode — component fetches and saves via campaign ID
interface ApiProps {
    campaignId: string;
    onSave?: () => void;
    readOnly?: boolean;
    showOnlyWeights?: boolean;
    stacked?: boolean;
    trainingType?: TrainingType;
    optionsTemplate?: RubricCategoryTemplate[];
    value?: never;
    onChange?: never;
}

export type RubricEditorProps = ControlledProps | ApiProps;

function LoadingSkeleton() {
    return (
        <div className="space-y-4 animate-pulse">
            {[1, 2, 3].map((i) => (
                <div
                    key={i}
                    className="rounded-lg border border-gray-200 p-6"
                >
                    <div className="flex gap-3">
                        <div className="w-5 h-10 bg-gray-200 rounded" />
                        <div className="flex-1 space-y-3">
                            <div className="h-5 bg-gray-200 rounded w-1/3" />
                            <div className="h-4 bg-gray-200 rounded w-2/3" />
                            <div className="h-4 bg-gray-200 rounded w-1/4" />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export function RubricEditor(props: RubricEditorProps) {
    const isControlled = props.value !== undefined;
    const { readOnly = false, stacked = false } = props;
    // Percentage layout (sum-to-100 weights + checkbox/scoring-guide customise
    // section). Available in BOTH controlled (create flow) and API mode (campaign
    // detail) — API mode adds its own Save footer below.
    const showOnlyWeights = props.showOnlyWeights === true;

    // API-mode-only state
    const [apiCriteria, setApiCriteria] = useState<RubricCriterion[]>([]);
    const [savedSnapshot, setSavedSnapshot] = useState<string>("");
    const [loading, setLoading] = useState(!isControlled);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Shared drag state
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    // Unified read/write
    const criteria: RubricCriterion[] = isControlled ? props.value : apiCriteria;

    // Good-answer options / scoring-guide defaults. An explicit `optionsTemplate`
    // (e.g. a client rubric) wins; otherwise fall back to the training type's
    // template, defaulting to "customer_service".
    const trainingType: TrainingType = props.trainingType ?? "customer_service";
    const optionsTemplate = props.optionsTemplate ?? RUBRIC_TEMPLATES[trainingType];
    const correctAnswerOptions = useMemo(() => correctAnswerOptionsFrom(optionsTemplate), [optionsTemplate]);
    const defaultScoringGuides = useMemo(() => scoringGuidesFrom(optionsTemplate), [optionsTemplate]);

    // showOnlyWeights mode state — must come after criteria is defined
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
    const [checkedMap, setCheckedMap] = useState<Record<string, boolean[]>>(() => initCheckedMap(criteria, correctAnswerOptions));
    const [customMap, setCustomMap] = useState<Record<string, string>>(() => initCustomMap(criteria, correctAnswerOptions));
    const [scoringMap, setScoringMap] = useState<Record<string, ScoringGuide>>(() => initScoringMap(criteria, defaultScoringGuides));

    const applyCriteria = (next: RubricCriterion[]) => {
        if (isControlled) {
            (props as ControlledProps).onChange(next);
        } else {
            setApiCriteria(next);
        }
    };

    const isDirty = !isControlled && JSON.stringify(apiCriteria) !== savedSnapshot;

    // API mode: load on mount
    const loadRubric = useCallback(async () => {
        if (isControlled) return;
        const { campaignId } = props as ApiProps;
        setLoading(true);
        setError(null);
        try {
            const data = await getRubric(campaignId);
            setApiCriteria(data);
            setSavedSnapshot(JSON.stringify(data));
        } catch {
            setError("Failed to load rubric. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [isControlled, props]);

    useEffect(() => {
        if (!isControlled) loadRubric();
    }, [loadRubric, isControlled]);

    // API mode loads criteria asynchronously, so the checkbox/custom/scoring
    // state maps (whose useState initializers ran against an empty list at mount)
    // must be re-seeded once the rubric arrives. Keyed on savedSnapshot so it
    // fires on load and after a save — not on every keystroke, so it never
    // clobbers in-progress edits.
    useEffect(() => {
        if (isControlled) return;
        setCheckedMap(initCheckedMap(apiCriteria, correctAnswerOptions));
        setCustomMap(initCustomMap(apiCriteria, correctAnswerOptions));
        setScoringMap(initScoringMap(apiCriteria, defaultScoringGuides));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedSnapshot, isControlled]);

    // API mode: save
    const handleSave = async () => {
        if (isControlled) return;
        const { campaignId, onSave } = props as ApiProps;
        setSaving(true);
        try {
            await updateRubric(campaignId, apiCriteria);
            setSavedSnapshot(JSON.stringify(apiCriteria));
            onSave?.();
        } catch {
            setError("Failed to save rubric. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    // Mutations
    const updateCriterion = (index: number, updates: Partial<RubricCriterion>) => {
        applyCriteria(criteria.map((c, i) => (i === index ? { ...c, ...updates } : c)));
    };

    const addCriterion = () => {
        applyCriteria([...criteria, createCriterion()]);
    };

    const removeCriterion = (index: number) => {
        applyCriteria(criteria.filter((_, i) => i !== index));
    };

    // Drag and drop
    const handleDragStart = (index: number) => setDraggedIndex(index);

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        setDragOverIndex(index);
    };

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }
        const updated = [...criteria];
        const [moved] = updated.splice(draggedIndex, 1);
        updated.splice(dropIndex, 0, moved);
        applyCriteria(updated);
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    if (loading) return <LoadingSkeleton />;

    if (error && criteria.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-sm text-red-600 mb-4">{error}</p>
                <Button variant="outline" size="sm" onClick={loadRubric}>
                    Retry
                </Button>
            </div>
        );
    }

    if (showOnlyWeights) {
        const weightSum = criteria.reduce((sum, c) => sum + c.weight, 0);
        const sumValid = weightSum === 100;

        const toggleExpanded = (id: string) => {
            setExpandedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
            });
        };

        return (
            <div className="space-y-2">
                {criteria.map((criterion, index) => {
                    const isExpanded = expandedIds.has(criterion.id);
                    const options = correctAnswerOptions[criterion.id] ?? [];
                    const checked = checkedMap[criterion.id] ?? options.map(() => true);
                    const custom = customMap[criterion.id] ?? "";
                    const scoring = scoringMap[criterion.id] ?? defaultScoringGuides[criterion.id] ?? { high: "", mid: "", low: "" };

                    return (
                        <div key={criterion.id} className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                            {/* Compact row */}
                            <div className="px-4 py-3">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-gray-900 mb-0.5">{criterion.name}</p>
                                        <p className="text-xs text-gray-500 leading-relaxed">{criterion.description}</p>
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
                                        <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            value={criterion.weight}
                                            disabled={readOnly}
                                            onChange={(e) => {
                                                if (readOnly) return;
                                                const val = Math.max(0, Math.min(100, parseInt(e.target.value) || 0));
                                                updateCriterion(index, { weight: val });
                                            }}
                                            className={cn(
                                                "w-14 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500",
                                                readOnly && "bg-gray-50 text-gray-500 cursor-not-allowed",
                                            )}
                                        />
                                        <span className="text-sm text-gray-400">%</span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => toggleExpanded(criterion.id)}
                                    className="mt-2.5 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                                >
                                    {isExpanded
                                        ? <><ChevronUpIcon className="w-3 h-3" /> Collapse</>
                                        : <><ChevronDownIcon className="w-3 h-3" /> Customise scoring</>
                                    }
                                </button>
                            </div>

                            {/* Expanded customise section */}
                            {isExpanded && (
                                <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-4 space-y-8">
                                    {/* Checkboxes */}
                                    <div>
                                        <p className="text-xs font-medium text-gray-600 mb-1">
                                            What a good answer looks like
                                        </p>
                                        <p className="text-xs text-gray-400 mb-2">Unchecked items won't be included in scoring.</p>
                                        <div className="space-y-2">
                                            {options.map((option, optIdx) => (
                                                <label
                                                    key={optIdx}
                                                    className={cn(
                                                        "flex items-start gap-2.5",
                                                        readOnly ? "cursor-default" : "cursor-pointer",
                                                    )}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={checked[optIdx] ?? true}
                                                        disabled={readOnly}
                                                        onChange={(e) => {
                                                            if (readOnly) return;
                                                            const next = checked.map((v, i) => i === optIdx ? e.target.checked : v);
                                                            setCheckedMap((prev) => ({ ...prev, [criterion.id]: next }));
                                                            updateCriterion(index, { correct_answer: assembleCorrectAnswer(criterion.id, next, custom, correctAnswerOptions) });
                                                        }}
                                                        className={cn(
                                                            "mt-0.5 h-4 w-4 rounded border-gray-300 accent-blue-600 focus:ring-blue-500",
                                                            readOnly && "cursor-not-allowed opacity-70",
                                                        )}
                                                    />
                                                    <span className={cn(
                                                        "text-sm leading-snug",
                                                        readOnly ? "text-gray-600" : "text-gray-700",
                                                    )}>{option}</span>
                                                </label>
                                            ))}
                                            {!readOnly && (
                                                <input
                                                    type="text"
                                                    value={custom}
                                                    onChange={(e) => {
                                                        setCustomMap((prev) => ({ ...prev, [criterion.id]: e.target.value }));
                                                        updateCriterion(index, { correct_answer: assembleCorrectAnswer(criterion.id, checked, e.target.value, correctAnswerOptions) });
                                                    }}
                                                    placeholder="Add your own..."
                                                    className="mt-1 w-full rounded-md border border-dashed border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-solid"
                                                />
                                            )}
                                        </div>
                                    </div>

                                    {/* Scoring guide */}
                                    <div>
                                        <p className="text-xs font-medium text-gray-600 mb-1">Scoring guide</p>
                                        <p className="text-xs text-gray-400 mb-2">The AI uses this to assign a score for each criterion.</p>
                                        <div className="space-y-3">
                                            {(["high", "mid", "low"] as const).map((level) => (
                                                <div key={level}>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">
                                                        {level === "high" ? "High score" : level === "mid" ? "Mid score" : "Low score"}
                                                    </label>
                                                    {/* Auto-growing textarea so the full (often multi-line)
                                                        scoring guide is always visible instead of being
                                                        clipped by a single-line input. The ref sizes it on
                                                        mount/value-change; onChange resizes while typing. */}
                                                    <textarea
                                                        value={scoring[level]}
                                                        disabled={readOnly}
                                                        rows={2}
                                                        ref={(el) => {
                                                            if (!el) return;
                                                            el.style.height = "auto";
                                                            el.style.height = `${el.scrollHeight}px`;
                                                        }}
                                                        onChange={(e) => {
                                                            if (readOnly) return;
                                                            e.target.style.height = "auto";
                                                            e.target.style.height = `${e.target.scrollHeight}px`;
                                                            const next = { ...scoring, [level]: e.target.value };
                                                            setScoringMap((prev) => ({ ...prev, [criterion.id]: next }));
                                                            updateCriterion(index, { scoring_guide: assembleScoringGuide(next) });
                                                        }}
                                                        className={cn(
                                                            "w-full resize-none rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500",
                                                            readOnly && "bg-gray-50 text-gray-500 cursor-not-allowed",
                                                        )}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Weight sum indicator */}
                <div className="flex justify-end">
                    <span className={cn(
                        "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium",
                        sumValid ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                    )}>
                        {sumValid ? "✓" : "⚠"} {weightSum}%{!sumValid && " · Adjust to reach 100%"}
                    </span>
                </div>

                {/* API-mode save footer. Disabled until the weights sum to 100 so
                    the trainer can't submit a rubric the BE would reject. */}
                {!isControlled && !readOnly && (
                    <div className="space-y-2">
                        {error && (
                            <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                                {error}
                            </div>
                        )}
                        <div className="flex justify-end">
                            <Button
                                onClick={handleSave}
                                disabled={!isDirty || saving || !sumValid}
                            >
                                {saving ? "Saving..." : "Save changes"}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (readOnly) {
        return (
            <div className="space-y-4">
                {error && (
                    <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                        {error}
                    </div>
                )}
                <div className="space-y-3">
                    {criteria.map((criterion) => (
                        <div key={criterion.id} className="rounded-lg border border-gray-200 bg-white p-5">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-semibold text-gray-900">
                                    {criterion.name || "Untitled criterion"}
                                </span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                                    {criterion.weight}%
                                </span>
                            </div>
                            {criterion.description && (
                                <p className="text-sm text-gray-500 mb-3">{criterion.description}</p>
                            )}
                            <div className={cn("grid gap-4 mt-3", stacked ? "grid-cols-1" : "grid-cols-2")}>
                                <div>
                                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Good answer looks like</p>
                                    {criterion.correct_answer ? (
                                        <ul className="space-y-1">
                                            {criterion.correct_answer.split("\n").filter(Boolean).map((line, i) => (
                                                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                                                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                                                    {line}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-sm text-gray-400 italic">Not specified</p>
                                    )}
                                </div>
                                <div>
                                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Scoring guide</p>
                                    {criterion.scoring_guide ? (
                                        <div className="space-y-3">
                                            {criterion.scoring_guide.split("\n").filter(Boolean).map((line, i) => {
                                                const isHigh = line.startsWith("High");
                                                const isMid = line.startsWith("Mid");
                                                const text = line.replace(SCORE_PREFIX, "");
                                                const label = isHigh ? "High" : isMid ? "Mid" : "Low";
                                                const cls = isHigh
                                                    ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                                    : isMid
                                                    ? "bg-amber-50 text-amber-700 border-amber-100"
                                                    : "bg-red-50 text-red-700 border-red-100";
                                                return (
                                                    <div key={i} className="grid grid-cols-[3rem_1fr] gap-2 text-sm">
                                                        <span className={cn("inline-flex items-center justify-center px-1.5 py-0.5 rounded border text-[10px] font-semibold", cls)}>
                                                            {label}
                                                        </span>
                                                        <span className="text-gray-700 leading-5">{text}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-400 italic">Not specified</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {error && (
                <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            <div className="space-y-3">
                {criteria.map((criterion, index) => (
                    <Card
                        key={criterion.id}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        className={cn(
                            "transition-all",
                            draggedIndex === index && "opacity-50",
                            dragOverIndex === index &&
                                draggedIndex !== index &&
                                "border-blue-500 border-2"
                        )}
                    >
                        <CardContent className="p-4">
                            <div className="flex gap-3">
                                <div className="flex items-start pt-2 cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500">
                                    <GripVerticalIcon className="w-5 h-5" />
                                </div>

                                <div className="flex-1 space-y-3">
                                    <input
                                        type="text"
                                        value={criterion.name}
                                        onChange={(e) =>
                                            updateCriterion(index, { name: e.target.value })
                                        }
                                        placeholder="e.g. Empathy"
                                        className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />

                                    <textarea
                                        value={criterion.description}
                                        onChange={(e) =>
                                            updateCriterion(index, { description: e.target.value })
                                        }
                                        placeholder="What does this criterion measure?"
                                        rows={1}
                                        className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />

                                    <div className="flex items-center gap-2">
                                        <label className="text-xs font-medium text-gray-500">
                                            Weight
                                        </label>
                                        <select
                                            value={criterion.weight}
                                            onChange={(e) =>
                                                updateCriterion(index, {
                                                    weight: parseFloat(e.target.value),
                                                })
                                            }
                                            className="rounded-md border border-gray-200 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            {WEIGHT_OPTIONS.map((w) => (
                                                <option key={w} value={w}>
                                                    {w.toFixed(1)}x
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="space-y-1">
                                        <div className="flex items-center gap-1.5">
                                            <label className="text-xs font-medium text-gray-500">
                                                What does a good answer look like?
                                            </label>
                                            <Tooltip content="Describe the ideal response so the AI knows what to look for when scoring.">
                                                <InfoIcon className="w-3.5 h-3.5 text-gray-400" />
                                            </Tooltip>
                                        </div>
                                        <textarea
                                            value={criterion.correct_answer}
                                            onChange={(e) =>
                                                updateCriterion(index, { correct_answer: e.target.value })
                                            }
                                            placeholder="Describe what the employee should say or do"
                                            rows={2}
                                            className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div className="space-y-1">
                                        <div className="flex items-center gap-1.5">
                                            <label className="text-xs font-medium text-gray-500">
                                                Scoring guide
                                            </label>
                                            <Tooltip content="Guide the AI on how to assign scores — e.g. Score 5 if..., 3 if..., 1 if...">
                                                <InfoIcon className="w-3.5 h-3.5 text-gray-400" />
                                            </Tooltip>
                                        </div>
                                        <textarea
                                            value={criterion.scoring_guide}
                                            onChange={(e) =>
                                                updateCriterion(index, { scoring_guide: e.target.value })
                                            }
                                            placeholder="Score 5 if..., 3 if..., 1 if..."
                                            rows={2}
                                            className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>

                                {criteria.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeCriterion(index)}
                                        className="text-gray-400 hover:text-red-500 transition-colors p-1 self-start mt-1"
                                        aria-label={`Remove ${criterion.name || "criterion"}`}
                                    >
                                        <TrashIcon className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={addCriterion}>
                    <PlusIcon className="w-4 h-4 mr-1" />
                    Add criterion
                </Button>

                {!isControlled && (
                    <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={!isDirty || saving}
                    >
                        <SaveIcon className="w-4 h-4 mr-1" />
                        {saving ? "Saving..." : "Save changes"}
                    </Button>
                )}
            </div>
        </div>
    );
}
